import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://solus-it.com.br";
const PAGE_URL = `${SITE_URL}/politica-de-privacidade`;

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${PAGE_URL}#webpage`,
      "url": PAGE_URL,
      "name": "Política de Privacidade & LGPD | Solus IT",
      "description":
        "Política de Privacidade da Solus IT conforme a LGPD (Lei nº 13.709/2018). Saiba como coletamos, usamos e protegemos seus dados pessoais.",
      "inLanguage": "pt-BR",
      "dateModified": "2026-04-04T00:00:00-03:00",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#organization` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Política de Privacidade", "item": PAGE_URL },
        ],
      },
    },
  ],
};

const sections = [
  {
    id: "introducao",
    title: "1. Introdução",
    content: (
      <>
        <p>
          A <strong>Solus IT Tecnologia</strong> (CNPJ 10.348.741/0001-11), com sede em Belém/PA e escritório em São Paulo/SP,
          está comprometida com a proteção dos seus dados pessoais e com o cumprimento da{" "}
          <strong>Lei Geral de Proteção de Dados Pessoais (LGPD — Lei nº 13.709/2018)</strong>.
        </p>
        <p className="mt-3">
          Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos
          seus dados ao interagir com nosso site <strong>solus-it.com.br</strong>.
        </p>
      </>
    ),
  },
  {
    id: "dados-coletados",
    title: "2. Dados Pessoais Coletados",
    content: (
      <>
        <p>Coletamos dados pessoais nas seguintes situações:</p>
        <ul className="mt-3 space-y-2 list-disc list-inside text-[#000000]/65">
          <li><strong className="text-[#000000]">Formulário de contato:</strong> nome completo, e-mail, telefone, nome da empresa e mensagem.</li>
          <li><strong className="text-[#000000]">Navegação no site:</strong> cookies de análise (Google Analytics/similar) que coletam dados anonimizados de acesso, como páginas visitadas, tempo de sessão e localização aproximada.</li>
          <li><strong className="text-[#000000]">WhatsApp:</strong> ao clicar no botão de contato, você será redirecionado ao WhatsApp; nenhum dado é coletado por nós nesse momento.</li>
        </ul>
        <p className="mt-3 text-sm">
          Não coletamos dados sensíveis (origem racial, convicções religiosas, dados de saúde, etc.).
        </p>
      </>
    ),
  },
  {
    id: "finalidade",
    title: "3. Finalidade do Tratamento",
    content: (
      <>
        <p>Utilizamos seus dados para as seguintes finalidades:</p>
        <div className="mt-4 space-y-3">
          {[
            { label: "Responder solicitações de contato", desc: "Retornar mensagens enviadas via formulário ou WhatsApp." },
            { label: "Envio de propostas comerciais", desc: "Apresentar soluções e orçamentos conforme o assunto selecionado." },
            { label: "Melhoria do site", desc: "Analisar dados de navegação para aperfeiçoar a experiência do usuário." },
            { label: "Cumprimento de obrigações legais", desc: "Atender exigências regulatórias e fiscais quando aplicável." },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(98,169,224,0.06)", border: "1px solid rgba(98,169,224,0.15)" }}>
              <div className="w-5 h-5 flex items-center justify-center mt-0.5" style={{ color: "#62A9E0" }}>
                <i className="ri-check-line text-sm"></i>
              </div>
              <div>
                <div className="text-sm font-semibold text-[#000000]">{item.label}</div>
                <div className="text-xs text-[#000000]/55 mt-0.5">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "base-legal",
    title: "4. Base Legal",
    content: (
      <>
        <p>O tratamento dos seus dados se fundamenta nas seguintes bases legais da LGPD:</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: "rgba(98,169,224,0.1)" }}>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-[#000000] rounded-tl-lg">Situação</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-[#000000] rounded-tr-lg">Base Legal (LGPD)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Formulário de contato", "Consentimento (art. 7º, I) / Legítimo interesse (art. 7º, IX)"],
                ["Envio de proposta comercial", "Execução de contrato ou diligências pré-contratuais (art. 7º, V)"],
                ["Cookies analíticos", "Consentimento (art. 7º, I)"],
                ["Obrigações legais e fiscais", "Cumprimento de obrigação legal (art. 7º, II)"],
              ].map(([sit, base], i) => (
                <tr key={sit} style={{ background: i % 2 === 0 ? "white" : "rgba(98,169,224,0.03)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <td className="px-4 py-2.5 text-[#000000]/70">{sit}</td>
                  <td className="px-4 py-2.5 text-[#000000]/70">{base}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: "compartilhamento",
    title: "5. Compartilhamento de Dados",
    content: (
      <>
        <p>
          Seus dados <strong>não são vendidos</strong> a terceiros. Podemos compartilhá-los apenas com:
        </p>
        <ul className="mt-3 space-y-2 list-disc list-inside text-[#000000]/65">
          <li><strong className="text-[#000000]">Prestadores de serviços tecnológicos</strong> (ex.: plataforma de hospedagem e e-mail) que tratam dados em nosso nome e sob nossas instruções.</li>
          <li><strong className="text-[#000000]">Autoridades competentes</strong> quando exigido por lei ou decisão judicial.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retencao",
    title: "6. Prazo de Retenção",
    content: (
      <p>
        Os dados coletados pelo formulário de contato são armazenados pelo prazo necessário para atender
        à sua solicitação e, no mínimo, por <strong>5 (cinco) anos</strong> para fins de guarda fiscal e
        cumprimento de obrigações legais, conforme legislação aplicável. Dados de cookies analíticos são
        retidos por até <strong>26 meses</strong>.
      </p>
    ),
  },
  {
    id: "direitos",
    title: "7. Seus Direitos como Titular",
    content: (
      <>
        <p>Conforme os artigos 17 a 22 da LGPD, você tem os seguintes direitos:</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { icon: "ri-eye-line", title: "Acesso", desc: "Saber quais dados seus possuímos." },
            { icon: "ri-edit-line", title: "Correção", desc: "Corrigir dados incompletos ou desatualizados." },
            { icon: "ri-delete-bin-line", title: "Exclusão", desc: "Solicitar a eliminação de dados desnecessários." },
            { icon: "ri-export-line", title: "Portabilidade", desc: "Receber seus dados em formato estruturado." },
            { icon: "ri-forbid-line", title: "Oposição", desc: "Opor-se ao tratamento em determinadas situações." },
            { icon: "ri-close-circle-line", title: "Revogação", desc: "Retirar consentimento a qualquer momento." },
          ].map((right) => (
            <div key={right.title} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(98,169,224,0.04)", border: "1px solid rgba(98,169,224,0.12)" }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ background: "rgba(98,169,224,0.12)", color: "#62A9E0" }}>
                <i className={`${right.icon} text-base`}></i>
              </div>
              <div>
                <div className="text-sm font-semibold text-[#000000]">{right.title}</div>
                <div className="text-xs text-[#000000]/55 mt-0.5">{right.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-[#000000]/65">
          Para exercer seus direitos, entre em contato pelo e-mail:{" "}
          <a href="mailto:privacidade@solus-it.com.br" className="font-medium hover:underline" style={{ color: "#4C86C2" }}>
            privacidade@solus-it.com.br
          </a>
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "8. Política de Cookies",
    content: (
      <>
        <p>
          Utilizamos cookies para melhorar sua navegação. Ao acessar o site pela primeira vez,
          você pode aceitar ou recusar cookies não essenciais por meio do banner de consentimento.
        </p>
        <div className="mt-4 space-y-3">
          {[
            { tipo: "Essenciais", desc: "Necessários para o funcionamento básico do site. Não podem ser desativados.", obrig: true },
            { tipo: "Analíticos", desc: "Coletam dados de uso para melhorar o desempenho e experiência do site.", obrig: false },
          ].map((c) => (
            <div key={c.tipo} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "white", border: "1px solid rgba(0,0,0,0.08)" }}>
              <div className="w-5 h-5 flex items-center justify-center mt-0.5" style={{ color: c.obrig ? "#16a34a" : "#62A9E0" }}>
                <i className={`${c.obrig ? "ri-lock-line" : "ri-bar-chart-line"} text-sm`}></i>
              </div>
              <div>
                <span className="text-sm font-semibold text-[#000000]">{c.tipo}</span>
                {c.obrig && <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full" style={{ background: "rgba(22,163,74,0.1)", color: "#16a34a" }}>Obrigatório</span>}
                <p className="text-xs text-[#000000]/55 mt-0.5">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "seguranca",
    title: "9. Segurança dos Dados",
    content: (
      <p>
        Adotamos medidas técnicas e organizacionais adequadas para proteger seus dados contra
        acesso não autorizado, perda, destruição ou divulgação indevida, incluindo conexões
        criptografadas (HTTPS), controle de acesso e monitoramento de segurança.
      </p>
    ),
  },
  {
    id: "encarregado",
    title: "10. Encarregado (DPO)",
    content: (
      <>
        <p>
          O encarregado pelo tratamento de dados pessoais da Solus IT pode ser contatado pelo e-mail:
        </p>
        <a
          href="mailto:privacidade@solus-it.com.br"
          className="inline-flex items-center gap-2 mt-3 text-sm font-medium hover:underline"
          style={{ color: "#4C86C2" }}
        >
          <i className="ri-mail-line"></i>
          privacidade@solus-it.com.br
        </a>
      </>
    ),
  },
  {
    id: "atualizacoes",
    title: "11. Atualizações desta Política",
    content: (
      <p>
        Esta Política pode ser atualizada periodicamente para refletir mudanças legais ou
        operacionais. A versão atualizada estará sempre disponível nesta página com a data
        de revisão. Recomendamos revisá-la regularmente.
      </p>
    ),
  },
];

export default function PoliticaPrivacidade() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO meta tags — React 19 hoists these to <head> */}
      <title>Política de Privacidade & LGPD | Solus IT</title>
      <meta
        name="description"
        content="Política de Privacidade da Solus IT em conformidade com a LGPD (Lei nº 13.709/2018). Consulte como seus dados pessoais são coletados, tratados e protegidos."
      />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={PAGE_URL} />
      <meta property="og:title" content="Política de Privacidade & LGPD | Solus IT" />
      <meta
        property="og:description"
        content="Política de Privacidade da Solus IT em conformidade com a LGPD (Lei nº 13.709/2018). Consulte como seus dados pessoais são coletados, tratados e protegidos."
      />
      <meta property="og:url" content={PAGE_URL} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Política de Privacidade & LGPD | Solus IT" />
      <meta
        name="twitter:description"
        content="Política de Privacidade da Solus IT conforme a LGPD — dados coletados, finalidade, direitos do titular e contato do DPO."
      />
      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16" style={{ background: "linear-gradient(135deg, #f8fbff 0%, #eef5fc 100%)" }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-5" style={{ color: "#4C86C2" }}>
            <div className="w-6 h-px" style={{ background: "linear-gradient(to right, #62A9E0, #4C86C2)" }}></div>
            Transparência &amp; Privacidade
            <div className="w-6 h-px" style={{ background: "linear-gradient(to right, #62A9E0, #4C86C2)" }}></div>
          </div>
          <h1 className="text-4xl font-black text-[#000000] mb-4">
            Política de <span className="solus-gradient-text">Privacidade</span>
          </h1>
          <p className="text-[#000000]/55 max-w-2xl mx-auto text-sm leading-relaxed">
            Saiba como a Solus IT coleta, utiliza e protege seus dados pessoais em conformidade
            com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).
          </p>
          <p className="mt-4 text-xs text-[#000000]/35">
            Última atualização: março de 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Index */}
          <div className="mb-12 p-6 rounded-lg" style={{ background: "rgba(98,169,224,0.05)", border: "1px solid rgba(98,169,224,0.18)" }}>
            <h2 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#4C86C2" }}>Sumário</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-sm text-[#000000]/65 hover:text-[#4C86C2] transition-colors">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {sections.map((s) => (
              <div key={s.id} id={s.id}>
                <h4 className="text-xl font-bold text-[#000000] mb-4 flex items-center gap-3">
                  <div className="w-1 h-6 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #62A9E0, #4C86C2)" }}></div>
                  <a href={`#${s.id}`}>{s.title}</a>
                </h4>
                <div className="text-sm text-[#000000]/65 leading-relaxed pl-4">{s.content}</div>
              </div>
            ))}
          </div>

          {/* Back */}
          <div className="mt-16 pt-8 border-t border-[#000000]/8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-md cursor-pointer whitespace-nowrap solus-btn-primary"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao site
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
